import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { AuthShell } from "./Login";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";

export default function Signup() {
  const { signupCompany } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ company_name: "", name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const data = await signupCompany(form);
      toast.success("Company created — awaiting approval");
      if (data?.company?.status && data.company.status !== "active") {
        navigate("/app/pending");
      } else {
        navigate("/app");
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail, "Signup failed"));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell>
      <div className="overline mb-2">SITESYNC / NEW COMPANY</div>
      <h1 className="text-3xl font-semibold tracking-tighter mb-8">Create your workspace.</h1>
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        <Field label="Company Name" testId="signup-company-input" value={form.company_name} onChange={update("company_name")} required />
        <Field label="Your Name" testId="signup-name-input" value={form.name} onChange={update("name")} required />
        <Field label="Work Email" type="email" testId="signup-email-input" value={form.email} onChange={update("email")} required />
        <Field label="Password" type="password" testId="signup-password-input" value={form.password} onChange={update("password")} required minLength={6} />
        {err && <div className="text-xs text-destructive font-mono border border-destructive/40 bg-destructive/5 px-3 py-2 rounded-sm">{err}</div>}
        <Button type="submit" disabled={busy} className="w-full rounded-sm h-11 bg-primary hover:bg-primary/90" data-testid="signup-submit-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create company & sign in"}
        </Button>
      </form>
      <div className="mt-6 text-sm text-muted-foreground">
        Already registered? <Link to="/login" className="text-foreground hover:text-primary">Sign in</Link>
      </div>
    </AuthShell>
  );
}

function Field({ label, testId, ...props }) {
  return (
    <div>
      <Label className="overline mb-2 block">{label}</Label>
      <Input {...props} data-testid={testId} className="rounded-sm font-mono" />
    </div>
  );
}
